

/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Material Dashboard 2 React base styles
import colors from "assets/theme-dark/base/colors";

// Material Dashboard 2 React helper functions
import pxToRem from "assets/theme-dark/functions/pxToRem";
import linearGradient from "assets/theme-dark/functions/linearGradient";

const { white, primary, grey, transparent } = colors;

// Kept vertically symmetric so the shadow doesn't read as an off-centre thumb.
const thumbShadow = "0 0 2px rgba(0, 0, 0, 0.32)";

// Metrics in px. The root is the track plus the padding that keeps it clear of the label,
// and the thumb is inset inside the track by `trackInset` on every side.
const thumbSize = 16;
const trackWidth = 36;
const trackHeight = 20;
const rootPaddingX = 8;
const rootPaddingY = 6;
const trackInset = (trackHeight - thumbSize) / 2;
const thumbTravel = trackWidth - thumbSize - trackInset * 2;

const switchButton = {
  defaultProps: {
    disableRipple: false,
    color: "primary",
    size: "small",
  },

  styleOverrides: {
    root: {
      width: pxToRem(trackWidth + rootPaddingX * 2),
      height: pxToRem(trackHeight + rootPaddingY * 2),
      padding: `${pxToRem(rootPaddingY)} ${pxToRem(rootPaddingX)}`,
      margin: pxToRem(4),
      display: "inline-flex",
      alignItems: "center",
      boxSizing: "border-box",
      overflow: "visible",
      verticalAlign: "middle",

      // MUI's `size` styles target these as descendants of the root, so they outrank the
      // slot overrides below. Anything that positions the thumb has to be declared here.
      // `switchBase` resolves against the root's padding box, hence the padding offsets.
      "& .MuiSwitch-switchBase": {
        top: pxToRem(rootPaddingY),
        left: pxToRem(rootPaddingX),
        padding: pxToRem(trackInset),

        "&.Mui-checked": {
          transform: `translateX(${pxToRem(thumbTravel)})`,
        },
      },

      "& .MuiSwitch-thumb": {
        width: pxToRem(thumbSize),
        height: pxToRem(thumbSize),
      },
    },

    switchBase: {
      color: grey[500],

      "&:hover": {
        backgroundColor: transparent.main,
      },

      "&.Mui-checked": {
        color: primary.main,

        "&:hover": {
          backgroundColor: transparent.main,
        },

        "& .MuiSwitch-thumb": {
          backgroundColor: `${white.main} !important`,
          boxShadow: thumbShadow,
        },

        "& + .MuiSwitch-track": {
          backgroundColor: `${primary.main} !important`,
          opacity: 1,
        },
      },

      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.38,
      },

      "&.Mui-focusVisible .MuiSwitch-thumb": {
        backgroundImage: linearGradient(primary.main, primary.main),
      },
    },

    thumb: {
      backgroundColor: white.main,
      boxShadow: thumbShadow,
      border: "none",
    },

    track: {
      width: pxToRem(trackWidth),
      height: pxToRem(trackHeight),
      borderRadius: pxToRem(trackHeight / 2),
      opacity: 1,
      backgroundColor: grey[600],
      border: "none",
    },

    checked: {},
  },
};

export default switchButton;
